import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'redux-bundler-react';

import AddressValidator from '@components/address-validator/AddressValidator';
import TextInput from '@components/text-input/TextInput';
import SelectInput from '@components/select/Select';
import CreatableSelectInput from '@components/new-inputs/creatableSelectInput';
import { Button, Grid, Label } from '@trussworks/react-uswds';
import Icon from '@components/icon/Icon';
import { mdiAccount } from '@mdi/js';

import { useFormContext } from 'react-hook-form';
import { salutations, countries } from '@forms/input-forms/helpers';
import {
  formatCoordFlt,
  formatCoordStr,
  formatUSPhoneNumber,
  mapCountryAlpha3toAlpha2,
  mapStateRegulation,
} from '@utils/helpers';
import PhoneInput from '@components/phone-input/PhoneInput';
import countryRegionData from 'country-region-data/dist/data-umd';

import './ContactFields.scss';
import AddressSuggestInput from '@components/address-suggestion/AddressSuggestInput';
import { ContactTypes, RequestType, statesData } from '@utils/enums.ts';
import { useUserProfile } from '@hooks';
import { useGeospatial } from '@stores';
import { useQuery } from '@tanstack/react-query';
import { PROJECT_QUERY_KEYS, ProjectQueries } from '@api/services/project/query.js';
import MultiSelectInput from '@components/multi-select/multiSelectInput';
import Section from '@components/section/Section';

import classnames from 'classnames';

const formatOptionLabel = (data) => (
  <>
    <div>
      <span className='text-bold'>
        {data.firstName} {data.lastName}
      </span>
    </div>
    <div>{data.emailAddress}</div>
  </>
);

const ContactFields = connect(
  'doUpdateRequestLocation',
  'selectProjectID',
  'selectRequestID',
  'selectVersion',
  'selectRequestType',
  ({
    doUpdateRequestLocation,
    projectID,
    requestID,
    version,
    requestType,
    handleFieldChange,
    isOptional = false,
    isReadOnly,
    label,
    requiredFields,
    requireEmail = true,
    showButton,
    showContactInfoSection = true,
    showSalutation = true,
    type,
  }) => {
    const { userProfileData } = useUserProfile();
    const { setAddressCandidates } = useGeospatial();

    const { data: sharedContacts, isFetching } = useQuery({
      queryKey: [PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS, projectID, requestID, version],
      queryFn: () => ProjectQueries.getSharedContacts(projectID, requestID, version),
      enabled: requestType !== RequestType.ViolationComplaint,
    });

    // Format shared contacts options list
    const contactsByType = sharedContacts?.filter(
      (item) => item.contactType === type && (item.firstName !== null || item.lastName !== null)
    );
    const sharedContactOptions = useMemo(() => {
      if (isFetching) return [];

      const formatFilteredSharedContacts = contactsByType?.map((item) => {
        const label = `${item.firstName} ${item.lastName} ${item?.emailAddress ? item?.emailAddress : ''}`;
        return {
          emailAddress: item.emailAddress,
          firstName: item.firstName,
          lastName: item.lastName,
          label: label, // @NOTE: Setting label used to enable react-select search feature
          value: item.contactID,
        };
      });

      return [
        {
          label: `Existing ${type}s`,
          options: formatFilteredSharedContacts,
        },
      ];
    }, [isFetching, contactsByType, type]);

    const { watch, setValue, register, trigger } = useFormContext();
    const [showPhoneTwo, setShowPhoneTwo] = useState(false);
    const [stateOptions, setStateOptions] = useState([]);
    const phoneRequired = requiredFields ? requiredFields.includes('Phone Number One') : true;

    register('showPhoneTwo');

    const phoneOne = watch('phoneOne');
    const phoneTwoType = watch('phoneTwoType');
    const phoneTwo = watch('phoneTwo');
    const faxPhone = watch('faxPhone');
    const address1 = watch('address');
    const address2 = watch('addressTwo');
    const city = watch('city');
    const state = watch('state');
    const zipcode = watch('zipcode');
    const country = watch('country');

    const states = mapStateRegulation(statesData);

    useEffect(() => {
      if ((phoneTwoType || phoneTwo) && !showPhoneTwo) {
        setShowPhoneTwo(() => true);
        setValue('showPhoneTwo', true);
      }
    }, [phoneTwoType, phoneTwo, showPhoneTwo, setValue]);

    const togglePhone = () => {
      if (!isReadOnly) {
        if (showPhoneTwo) {
          setShowPhoneTwo(() => false);
          setValue('showPhoneTwo', false);
          setValue('phoneTwo', '', { shouldValidate: true, shouldDirty: true });
          setValue('phoneTwoCountryCode', '', { shouldValidate: true, shouldDirty: true });
          setValue('phoneTwoType', '', { shouldValidate: true, shouldDirty: true });
          setValue('phoneTwoExtension', '', { shouldValidate: true, shouldDirty: true });
        } else {
          setShowPhoneTwo(() => true);
          setValue('showPhoneTwo', true, { shouldValidate: true, shouldDirty: true });
          setValue('phoneTwoCountryCode', country === 'US' ? '1' : '', { shouldValidate: true, shouldDirty: true });
        }
      }
    };

    const populateContact = (contactData) => {
      setValue('contactID', null, { shouldValidate: true, shouldDirty: true });
      setValue('firstName', contactData?.firstName ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('middleName', contactData?.middleName ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('lastName', contactData?.lastName ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('address', contactData?.address ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('addressTwo', contactData?.addressTwo ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('city', contactData?.city ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('country', contactData?.country ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('state', contactData?.state ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('zipcode', contactData?.zipcode ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('company', contactData?.companyName ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('phoneOneType', contactData?.phoneOneType ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('phoneOneCountryCode', contactData?.phoneOneCountryCode ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        'phoneOne',
        contactData?.phoneOne
          ? contactData?.phoneOneCountryCode === '1'
            ? formatUSPhoneNumber(contactData?.phoneOne)
            : contactData?.phoneOne
          : null,
        { shouldValidate: true, shouldDirty: true }
      );
      setValue('phoneTwoType', contactData?.phoneTwoType ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('phoneTwoCountryCode', contactData?.phoneTwoCountryCode ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        'phoneTwo',
        contactData?.phoneTwo
          ? contactData?.phoneTwoCountryCode === '1'
            ? formatUSPhoneNumber(contactData?.phoneTwo)
            : contactData?.phoneTwo
          : null,
        { shouldValidate: true, shouldDirty: true }
      );
      setValue('faxCountryCode', contactData?.faxCountryCode ?? null, { shouldValidate: true, shouldDirty: true });
      setValue(
        'faxPhone',
        contactData?.faxPhone
          ? contactData?.faxCountryCode === '1'
            ? formatUSPhoneNumber(contactData?.faxPhone)
            : contactData?.faxPhone
          : null,
        { shouldValidate: true, shouldDirty: true }
      );
      setValue('salutation', contactData?.salutation ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('emailAddress', contactData?.emailAddress ?? null, { shouldValidate: true, shouldDirty: true });
      handleFieldChange && handleFieldChange();
    };

    const populateAddressFields = (address, isProjectAddress) => {
      const fields = address?.candidates?.[0]?.attributes;
      const addressField = document.getElementById('address');
      const addressFieldFocused = document.activeElement === addressField;

      if (fields) {
        if (addressFieldFocused) {
          addressField.blur();
        }

        setValue('address', fields.StAddr, { shouldValidate: true, shouldDirty: true });
        setValue('addressTwo', fields.SubAddr, { shouldValidate: true, shouldDirty: true });
        setValue('city', fields.City, { shouldValidate: true, shouldDirty: true });
        setValue('state', fields.RegionAbbr, { shouldValidate: true, shouldDirty: true });
        setValue('zipcode', fields.Postal, { shouldValidate: true, shouldDirty: true });
        setValue('county', fields.Subregion, { shouldValidate: true, shouldDirty: true });
        setValue('country', mapCountryAlpha3toAlpha2(fields.Country), { shouldValidate: true, shouldDirty: true });
        if (isProjectAddress) {
          setValue('latitude', formatCoordStr(fields.Y), { shouldValidate: true, shouldDirty: true });
          setValue('longitude', formatCoordStr(fields.X), { shouldValidate: true, shouldDirty: true });
          doUpdateRequestLocation({
            address: fields?.StAddr,
            addressTwo: fields?.SubAddr,
            city: fields?.City,
            state: fields?.RegionAbbr,
            zipcode: fields?.Postal,
            county: fields?.Subregion,
            country: fields?.Country !== 'USA' ? fields?.Country : 'US',
            latitude: formatCoordFlt(fields.Y),
            longitude: formatCoordFlt(fields.X),
          });
        } else {
          setAddressCandidates([]);
        }

        if (addressFieldFocused) {
          addressField.focus();
        }
      }
    };

    const populateFromExisting = (e) => {
      if (e === null) {
        // Input was cleared/null
        populateContact(null);
      } else if (e?.value) {
        // Mutliselect selection
        populateContact(sharedContacts.filter((item) => item.contactID === Number(e?.value))[0]);
      } else {
        // Use Profile button
        populateContact(userProfileData);
      }
    };

    const addressValues = useMemo(
      () => ({
        address1: address1,
        address2: address2,
        city: city,
        state: state,
        zipcode: zipcode,
        country: country,
      }),
      [address1, address2, city, state, zipcode, country]
    );

    const zipRequired = useMemo(() => {
      const countryNotForeign = country === 'US' || !country;
      const required = requiredFields ? requiredFields.includes('Zip Code') : true;

      return countryNotForeign && required;
    }, [country, requiredFields]);

    const handleBlur = (e) => {
      setValue(e?.target?.name, e?.target?.value?.trim(), { shouldValidate: true, shouldDirty: true });

      if (e?.target?.name === 'address' && e?.target?.value?.trim()) {
        setValue('country', 'US');
      }
    };

    const regionHelper = useCallback((country) => {
      const selectedCountryData = countryRegionData?.find(
        (regionCountry) => regionCountry.countryShortCode === country
      );

      let regions = [];
      if (selectedCountryData) {
        regions = selectedCountryData.regions.map((region) => ({
          value: region.shortCode ?? region.name, // Some countries do not have provided shortcodes
          text: region.name,
        }));
      }

      regions.push({ value: 'Other', text: 'Other' });
      regions.push({ value: 'None', text: 'None' });

      return regions;
    }, []);

    const handleCountryChange = useCallback(
      (selectedCountry) => {
        const regions = regionHelper(selectedCountry);
        setStateOptions(regions);

        if (selectedCountry !== country) {
          trigger('address');
          setValue('city', '', { shouldValidate: true, shouldDirty: true });
          setValue('state', '', { shouldValidate: true, shouldDirty: true });
          setValue('zipcode', '', { shouldValidate: true, shouldDirty: true });
        }

        if (selectedCountry === 'US' && !phoneOne) {
          setValue('phoneOneCountryCode', '1', { shouldValidate: true, shouldDirty: true });
        } else if (selectedCountry !== 'US' && !phoneOne) {
          setValue('phoneOneCountryCode', '', { shouldValidate: true, shouldDirty: true });
        }

        if (selectedCountry === 'US' && !phoneTwo && showPhoneTwo) {
          setValue('phoneTwoCountryCode', '1', { shouldValidate: true, shouldDirty: true });
        } else if (selectedCountry !== 'US' && !phoneTwo && showPhoneTwo) {
          setValue('phoneTwoCountryCode', '', { shouldValidate: true, shouldDirty: true });
        }

        if (selectedCountry === 'US' && !faxPhone) {
          setValue('faxCountryCode', '1', { shouldValidate: true, shouldDirty: true });
        } else if (selectedCountry !== 'US' && !faxPhone) {
          setValue('faxCountryCode', '', { shouldValidate: true, shouldDirty: true });
        }
      },
      [country, phoneOne, phoneTwo, showPhoneTwo, faxPhone, regionHelper, setStateOptions, setValue, trigger]
    );

    useEffect(() => {
      const regions = regionHelper(country);
      setStateOptions(regions);
    }, [country, regionHelper, setStateOptions]);

    useEffect(() => {
      const matchingOption = stateOptions?.find((option) => option?.value === state);
      if (matchingOption) {
        setValue('state', matchingOption?.value, { shouldValidate: true, shouldDirty: true });
      }
    }, [stateOptions, setValue, state]);

    return (
      <Section label={label}>
        {showButton && !isReadOnly && (
          <Button className='use-profile-button' onClick={populateFromExisting} tabIndex={0} disabled={isReadOnly}>
            {' '}
            <Icon focusable={false} title='Use Profile Data' path={mdiAccount} size={0.85} />
            USE PROFILE DATA{' '}
          </Button>
        )}
        {/* NOTE: Show only if there are existing shared contacts */}
        {contactsByType?.length > 0 && !isReadOnly && (
          <MultiSelectInput
            closeMenuOnSelect
            formatOptionLabel={formatOptionLabel}
            handleChange={populateFromExisting}
            isLoading={isFetching}
            label='Choose an Existing Contact'
            name='sharedContact'
            options={sharedContactOptions}
            styles={{
              container: (provided) => ({ ...provided, width: '25vw' }),
              menu: (provided) => ({ ...provided, width: '25vw' }),
              menuList: (provided) => ({ ...provided, maxHeight: '43vh' }),
            }}
            isClearable
          />
        )}
        {type === ContactTypes.PropertyOwner && (
          <Section isSubsection label={`${label} Parcel Number(s)`}>
            <CreatableSelectInput
              name={'parcels'}
              label='Parcel Number(s)'
              placeholder='Enter any applicable parcel numbers'
              multi
              required={requiredFields ? requiredFields.includes('Parcel Number(s)') : false}
              readOnly={isReadOnly}
              onChange={handleFieldChange}
            />
          </Section>
        )}
        <Section isSubsection label={`${label} Name`} headerClassName='no-top-margin'>
          {showSalutation === true && (
            <Grid row gap='md'>
              <Grid tablet={{ col: 'auto' }}>
                <SelectInput name='salutation' label='Salutation' readOnly={isReadOnly} onChange={handleFieldChange}>
                  {salutations.map((item, i) => (
                    <option key={i + 2} value={item.value}>
                      {item.text}
                    </option>
                  ))}
                </SelectInput>
              </Grid>
            </Grid>
          )}
          <Grid row gap='md'>
            <Grid tablet={{ col: 4 }}>
              <TextInput
                label='First Name'
                name='firstName'
                required={requiredFields ? requiredFields.includes('First Name') : true}
                readOnly={isReadOnly}
                onBlur={handleBlur}
                onChange={handleFieldChange}
              />
            </Grid>
            <Grid tablet={{ col: 4 }}>
              <TextInput
                label='Middle Name'
                name='middleName'
                readOnly={isReadOnly}
                onBlur={handleBlur}
                onChange={handleFieldChange}
              />
            </Grid>
            <Grid tablet={{ col: 4 }}>
              <TextInput
                label='Last Name'
                name='lastName'
                required={requiredFields ? requiredFields.includes('Last Name') : true}
                readOnly={isReadOnly}
                onBlur={handleBlur}
                onChange={handleFieldChange}
              />
            </Grid>
          </Grid>
        </Section>
        <Section
          headerClassName='no-top-margin'
          isSubsection
          label={`${label} Address ${isOptional ? <span className='text-italic'> (optional)</span> : ''}`}
        >
          <Grid row gap='md'>
            <Grid tablet={{ col: 8 }}>
              <AddressSuggestInput
                id='address'
                name='address'
                label='Address'
                required={requiredFields ? requiredFields.includes('Address One') : true}
                usOnly={false}
                onBlur={handleBlur}
                onChange={handleFieldChange}
                populateAddressFields={populateAddressFields}
                readOnly={isReadOnly}
                showOptionalText={false}
              />
            </Grid>
          </Grid>
          <Grid row gap='md'>
            <Grid tablet={{ col: 8 }}>
              <TextInput
                label='Address Line 2'
                name='addressTwo'
                readOnly={isReadOnly}
                onBlur={handleBlur}
                onChange={handleFieldChange}
                showOptionalText={!isOptional}
              />
            </Grid>
          </Grid>
          <Grid row gap='md'>
            <Grid tablet={{ col: 'auto' }}>
              <SelectInput
                name='country'
                label='Country'
                required={requiredFields ? requiredFields.includes('Country') : true}
                readOnly={isReadOnly}
                onChange={(e) => handleCountryChange(e.target.value)}
                showOptionalText={false}
              >
                {countries?.map((item, i) => (
                  <option key={i + 2} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </SelectInput>
            </Grid>
          </Grid>
          <Grid row gap='md'>
            <Grid tablet={{ col: 4 }}>
              <TextInput
                label='City'
                name='city'
                required={requiredFields ? requiredFields.includes('City') : true}
                readOnly={isReadOnly}
                onBlur={handleBlur}
                onChange={handleFieldChange}
                showOptionalText={false}
              />
            </Grid>
            {country === 'US' || country === null || country === '' ? (
              <Grid tablet={{ col: 4 }}>
                <SelectInput
                  name='state'
                  label='State'
                  required={requiredFields ? requiredFields.includes('State') : true}
                  readOnly={isReadOnly}
                  onChange={handleFieldChange}
                  showOptionalText={false}
                >
                  {states?.map((item, i) => (
                    <option key={i + 2} value={item.value}>
                      {item.text}
                    </option>
                  ))}
                </SelectInput>
              </Grid>
            ) : (
              <Grid tablet={{ col: 4 }}>
                <SelectInput
                  name='state'
                  label='Region'
                  required={requiredFields ? requiredFields.includes('State') : true}
                  readOnly={isReadOnly}
                  showOptionalText={false}
                >
                  {stateOptions?.map((item, i) => (
                    <option key={i + 2} value={item?.value}>
                      {item?.text}
                    </option>
                  ))}
                </SelectInput>
              </Grid>
            )}
            <Grid tablet={{ col: 4 }}>
              <TextInput
                type='text'
                label={country === 'US' || country === null || country === '' ? 'Zip Code' : 'Postal code'}
                required={zipRequired}
                name='zipcode'
                maxLength={country === 'US' ? 5 : 10}
                readOnly={isReadOnly}
                onChange={handleFieldChange}
                showOptionalText={!isOptional && !(country === 'US' || country === null)}
              />
            </Grid>
          </Grid>
          {!isReadOnly && (
            <Grid row className='margin-top-2' gap='md'>
              <Grid tablet={{ col: 12 }}>
                <AddressValidator source='form' isDisabled={isReadOnly} address={addressValues} />
              </Grid>
            </Grid>
          )}
        </Section>
        {showContactInfoSection && (
          <Section isSubsection label={`${label} Contact Information`} headerClassName='no-top-margin'>
            <fieldset className='padding-0' id='phone-info'>
              <Label
                className={classnames({
                  required: phoneRequired,
                  optional: !phoneRequired,
                })}
              >
                Phone Number
              </Label>
              <div className='phone-container phone1'>
                <PhoneInput
                  phoneName='phoneOne'
                  phoneCountryCodeName='phoneOneCountryCode'
                  phoneTypeName='phoneOneType'
                  extensionName='phoneOneExtension'
                  required={requiredFields ? requiredFields.includes('Phone Number One') : false}
                  readOnly={isReadOnly}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                  showTypeSelect
                />
                {!showPhoneTwo && !isReadOnly && (
                  <div className='phone-button-container'>
                    <Button
                      className='button-small add-phone-button'
                      disabled={isReadOnly}
                      size='small'
                      title='Add Phone Number'
                      id='phone-toggle-1'
                      name='phoneToggleButton1'
                      onClick={togglePhone}
                      onKeyUp={(e) => {
                        e.key === 'Enter' && togglePhone();
                      }}
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>
              {showPhoneTwo && (
                <div className='phone-container phone2'>
                  <PhoneInput
                    phoneName='phoneTwo'
                    phoneCountryCodeName='phoneTwoCountryCode'
                    phoneTypeName='phoneTwoType'
                    extensionName='phoneTwoExtension'
                    required={requiredFields ? requiredFields.includes('Phone Number Two') : true}
                    readOnly={isReadOnly}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    showTypeSelect
                  />
                  {!isReadOnly && (
                    <div className='phone-button-container'>
                      <Button
                        className='button-small'
                        disabled={isReadOnly}
                        title='Remove Phone Number'
                        id='phone-toggle-2'
                        name='phoneToggleButton2'
                        onClick={togglePhone}
                        secondary
                        onKeyUp={(e) => {
                          e.key === 'Enter' && togglePhone();
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </fieldset>
            <div>
              <PhoneInput
                phoneName='faxPhone'
                phoneCountryCodeName='faxCountryCode'
                label='Fax Number'
                readOnly={isReadOnly}
                onChange={handleFieldChange}
                onBlur={handleBlur}
              />
            </div>
            <Grid row gap='md'>
              <Grid col>
                <TextInput
                  name='emailAddress'
                  label='Email Address'
                  required={requireEmail && (requiredFields ? requiredFields.includes('Email Address') : true)}
                  readOnly={isReadOnly}
                  onBlur={handleBlur}
                  onChange={handleFieldChange}
                />
              </Grid>
            </Grid>
            <Grid row gap='md'>
              <Grid col>
                <TextInput
                  name='company'
                  label='Company'
                  readOnly={isReadOnly}
                  onBlur={handleBlur}
                  onChange={handleFieldChange}
                />
              </Grid>
            </Grid>
          </Section>
        )}
      </Section>
    );
  }
);

export default ContactFields;
